import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Users, Hotel, CheckCircle, XCircle, Clock, Phone, Mail } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatCurrency = (value) => {
  return `₹${(value || 0).toLocaleString()}`;
};

const QuoteView = () => {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/public/quotes/${id}`);
      setQuote(response.data);
    } catch (err) {
      console.error("Error fetching quote:", err);
      setError("Quote not found or expired.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-8">
          <div className="bg-primary-700 h-32 relative">
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute bottom-6 left-8 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-white/20 text-white hover:bg-white/30 border-none">
                  Quote #{quote.quote_number || quote.id.slice(0, 8).toUpperCase()}
                </Badge>
                <span className="text-sm opacity-90">{formatDate(quote.created_at)}</span>
              </div>
              <h1 className="text-3xl font-bold">{quote.destination} Getaway</h1>
            </div>
          </div>
          
          <div className="p-8">
            <div className="grid md:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-primary-600 mt-1" />
                <div>
                  <p className="text-sm text-slate-500">Travel Dates</p>
                  <p className="font-semibold text-slate-900">{formatDate(quote.travel_date)}</p>
                  <p className="text-xs text-slate-400">to {formatDate(quote.return_date)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-primary-600 mt-1" />
                <div>
                  <p className="text-sm text-slate-500">Guests</p>
                  <p className="font-semibold text-slate-900">
                    {quote.pax_adults} Adults
                    {quote.pax_children > 0 && `, ${quote.pax_children} Kids`}
                  </p>
                  <p className="text-xs text-slate-400">{quote.no_of_rooms || 1} Rooms</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary-600 mt-1" />
                <div>
                  <p className="text-sm text-slate-500">Destination</p>
                  <p className="font-semibold text-slate-900">{quote.destination}</p>
                  <p className="text-xs text-slate-400">{quote.covered_cities || "Multiple Cities"}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Hotel className="w-5 h-5 text-primary-600 mt-1" />
                <div>
                  <p className="text-sm text-slate-500">Hotel Category</p>
                  <p className="font-semibold text-slate-900 capitalize">{quote.hotel_category || "Standard"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Itinerary */}
            <Card className="border-none shadow-sm">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary-600" /> Day-wise Itinerary
                </h2>
                <div className="prose prose-slate max-w-none text-sm whitespace-pre-wrap leading-relaxed">
                  {quote.itinerary || "Detailed itinerary will be shared shortly."}
                </div>
              </CardContent>
            </Card>

            {/* Inclusions & Exclusions */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-none shadow-sm bg-green-50/50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" /> Inclusions
                  </h3>
                  <div className="text-sm text-green-900/80 whitespace-pre-wrap">
                    {quote.inclusions || "Flight, Hotel, Transfers, Breakfast"}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-sm bg-red-50/50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-red-800 mb-4 flex items-center gap-2">
                    <XCircle className="w-5 h-5" /> Exclusions
                  </h3>
                  <div className="text-sm text-red-900/80 whitespace-pre-wrap">
                    {quote.exclusions || "Personal expenses, Tips, anything not mentioned"}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="space-y-6">
            {/* Pricing Card */}
            <Card className="border-none shadow-lg bg-white sticky top-24">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-slate-500 text-sm mb-1">Total Package Cost</p>
                  <p className="text-4xl font-bold text-primary-700">
                    {formatCurrency(quote.quote_amount)}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">*Inclusive of all taxes</p>
                </div>

                <div className="space-y-3 mb-6 bg-slate-50 p-4 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Per Adult</span>
                    <span className="font-medium">{formatCurrency(quote.quote_per_adult)}</span>
                  </div>
                  {quote.pax_children > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Per Child</span>
                      <span className="font-medium">{formatCurrency(quote.quote_per_child)}</span>
                    </div>
                  )}
                </div>

                <Button className="w-full bg-primary-700 hover:bg-primary-800 h-12 text-lg">
                  Book Now
                </Button>
                
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <p className="text-center text-sm text-slate-500 mb-4">Questions? Contact us</p>
                  <div className="flex justify-center gap-4">
                    <Button variant="outline" size="sm" className="gap-2">
                      <Phone className="w-4 h-4" /> Call
                    </Button>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Helper Badge Component
const Badge = ({ children, className }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}>
    {children}
  </span>
);

export default QuoteView;
